// Stock's catalog (pages/ux-full components/stock.js CatalogTable): the price book's parts as stock keeps
// them, drawn from stock.catalog's answer. Each part in its own unit and what the vendor sells it in (a
// box of 10, a coil of 300 ft), its bin and vendor, then our cost, its price and the margin. Cost and
// margin only when the answer carries our cost (pricebook.cost.view). Our own parts, kept but never
// sold, and ones no longer offered say so in words. One action a row: the part's name opens it.
import type { CatalogPart, CatalogTableProps } from '../stock-schemas.ts';
import { money } from '../format.ts';
import { Action } from '../primitives.tsx';
import { useShown } from './AdsViews.tsx';

/** "10.000" → 10, "2.500" → 2.5 */
const qty = (s: string) => Number(s);
const fmt = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 3 });

/** Price less cost as a share of price, whole percent; null without both. */
export function marginOf(p: Pick<CatalogPart, 'priceCents' | 'costCents' | 'forSale'>): number | null {
  if (!p.forSale || p.costCents == null || p.priceCents <= 0) return null;
  return Math.round(((p.priceCents - p.costCents) / p.priceCents) * 100);
}

/** "each, by the box of 10" or "ft, bought singly". */
export function unitWords(p: CatalogPart): string {
  const per = qty(p.stock.unitsPerPurchase);
  return p.stock.purchaseUnit && per !== 1
    ? `${p.unit}, by the ${p.stock.purchaseUnit} of ${fmt(per)}`
    : `${p.unit}, bought singly`;
}

function aboutPart(p: CatalogPart): string {
  const kept = p.stock.tracked
    ? p.stock.bin
      ? `${p.code}, bin ${p.stock.bin}`
      : p.code
    : `${p.code}. Not kept in stock: used at its cost, never counted`;
  return [kept, p.stock.vendor ? `From ${p.stock.vendor.name}` : null].filter(Boolean).join('. ');
}

export function CatalogTable({
  data,
  onOpen,
  onMore,
  empty = 'No parts in the price book yet. Add one in Setup › Price book.',
}: {
  data: CatalogTableProps;
  /** Opens one part: its units, vendor, bin and levels (the screen's). */
  onOpen?: (p: CatalogPart) => void;
  /** The next page, when there is one (stock.catalog's next). */
  onMore?: () => void;
  empty?: string;
}) {
  const [rows, more] = useShown(data.parts, 5);
  // the server leaves our cost out for whoever may not see it: so do the cost and margin columns
  const cost = data.parts.some((p) => 'costCents' in p);
  if (!data.parts.length)
    return (
      <p className="flw-empty" role="status">
        {empty}
      </p>
    );
  return (
    <>
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>Part</th>
              <th>Kept in, bought by</th>
              {cost && <th className="flw-num">Cost</th>}
              <th className="flw-num">Price</th>
              {cost && <th className="flw-num">Margin</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const per = qty(p.stock.unitsPerPurchase);
              const m = marginOf(p);
              return (
                <tr key={p.id} className={p.active ? undefined : 'flw-off'}>
                  <th scope="row">
                    {onOpen ? (
                      <button type="button" className="flw-open" onClick={() => onOpen(p)}>
                        {p.name}
                      </button>
                    ) : (
                      <span className="flw-open">{p.name}</span>
                    )}
                    <span className="flw-sub">{aboutPart(p)}</span>
                    {!p.forSale && <span className="flw-sub">Ours only: kept and used, never sold</span>}
                    {!p.active && <span className="flw-sub">No longer offered</span>}
                  </th>
                  <td>
                    {unitWords(p)}
                    {cost && p.costCents != null && p.stock.purchaseUnit && per !== 1 && (
                      <span className="flw-sub">
                        {money(Math.round(p.costCents * per))} a {p.stock.purchaseUnit}
                      </span>
                    )}
                  </td>
                  {cost && <td className="flw-num">{p.costCents == null ? '—' : money(p.costCents)}</td>}
                  <td className="flw-num">{p.forSale ? money(p.priceCents) : 'Not for sale'}</td>
                  {cost && <td className="flw-num">{m === null ? '—' : `${m} %`}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {more ??
        (onMore && (
          <p className="flw-more">
            <Action onClick={onMore}>Show more</Action>
          </p>
        ))}
    </>
  );
}
